/**
 * Centralized Document Generator
 *
 * Provides a unified interface for generating documents in both PDF and DOCX formats.
 * Ensures content parity between formats and handles serialization for localStorage persistence.
 */

import type { BuildFormattedPdfParams, FormattedPdfTranslationOverrides } from './formattingPdf';
import type { FormattedDocxTranslationOverrides } from './formattingDocx';
import type { PdfTranslationOverrides } from './reportPdf';
import type { DocxTranslationOverrides } from './reportDocx';
import type * as DocumentBuildersModule from './documentBuilders';
import { retryImport } from './lazyWithRetry';

// ============================================================================
// The builders, loaded the first time a document is made (2026-09-25)
// ============================================================================

/**
 * The Word and PDF builders (docx, pdf-lib and its fonts, about 770 KB before
 * compression) are their own chunk, `document-builders`, fetched the first time
 * anything asks for a document. They used to ride the first download, the file
 * every visitor to prest0.ai waits for before the page can paint, reached
 * through the connect flow's sign-in helpers, for work only a signed-in person
 * asking for a file ever does. Building a document already takes seconds, and
 * the fetch starts at the top of the build, so the wait is one download at the
 * moment someone is waiting for a file anyway.
 *
 * This is the chunk's one door (scripts/check-chat-first-paint.mjs, R4, row
 * `document-builders`). A failed load is forgotten, so the next document tries
 * again instead of replaying a cached rejection; `retryImport` has already
 * retried it and handed a chunk that is gone for good to the stale-tab
 * self-heal.
 */
type DocumentBuilders = typeof DocumentBuildersModule;

const BUILDERS_LOAD_RETRIES = 3;
const BUILDERS_LOAD_RETRY_DELAY_MS = 1000;

let buildersLoading: Promise<DocumentBuilders> | null = null;

export function loadDocumentBuilders(): Promise<DocumentBuilders> {
  if (!buildersLoading) {
    buildersLoading = retryImport(
      () => import(/* webpackChunkName: "document-builders" */ './documentBuilders'),
      BUILDERS_LOAD_RETRIES,
      BUILDERS_LOAD_RETRY_DELAY_MS
    ).catch((err: unknown) => {
      buildersLoading = null;
      throw err;
    });
  }
  return buildersLoading;
}

// ============================================================================
// Types
// ============================================================================

/**
 * Serialized attachment for localStorage persistence.
 * Matches the SerializedAttachment interface from GlobalChatSystem/types.ts
 */
export interface SerializedAttachment {
  name: string;
  type: string;
  size: number;
  lastModified?: number;
  /** Data URL (base64) for localStorage persistence */
  dataUrl?: string;
  /** Firebase Storage download URL for cross-device persistence */
  storageUrl?: string;
  /** Firebase Storage path for reference/deletion */
  storagePath?: string;
}

/**
 * Result of dual-format document generation.
 */
export interface GeneratedDocuments {
  /** PDF File object */
  pdf: File;
  /** DOCX File object */
  docx: File;
  /** Serialized PDF for localStorage */
  pdfSerialized: SerializedAttachment;
  /** Serialized DOCX for localStorage */
  docxSerialized: SerializedAttachment;
}

/**
 * Parameters for generating formatted documents (for wider audience).
 */
export interface FormattedDocumentParams {
  type: 'formatted';
  /** The formatted report content */
  content: string;
  /** Document title */
  title: string;
  /** Date of creation */
  createdAt?: Date;
  /** Optional translation overrides */
  translations?: FormattedPdfTranslationOverrides & FormattedDocxTranslationOverrides;
  /**
   * Emit the generated cover page. Defaults to true.
   * Pass false when `content` is a document the USER supplied (e.g. a
   * translated PDF): it already carries its own title page, and prepending
   * ours produced the "broken first page" reported on 2026-08-13.
   */
  includeCoverPage?: boolean;
  /** Figures extracted from the SOURCE document, tagged with their page. */
  sourceImages?: Array<{
    pageNumber: number;
    base64: string;
    widthPx: number;
    heightPx: number;
    /** Where the figure sits in its page's text flow; see SourcePageImage. */
    afterCharRatio?: number;
  }>;
  /**
   * Called as the deliverables are built and saved.
   *
   * WHY (2026-08-21 sweep): building the files and uploading them are THREE
   * long steps behind ONE static label — the PDF, the Word file, then an
   * upload that for the scanned record was 27MB. #989 gave the build a label
   * but nothing that moves, which the third instance of this defect taught us
   * is not progress.
   */
  onProgress?: (info: { stage: 'pdf' | 'docx' | 'saving'; pages?: number }) => void;
}

/**
 * Parameters for generating research documents (legal research format).
 */
export interface ResearchDocumentParams {
  type: 'research';
  /** Document title */
  title: string;
  /** Original research query/prompt */
  prompt: string;
  /** Research content */
  content: string;
  /** Date of creation */
  createdAt?: Date;
  /** Optional translation overrides */
  translations?: PdfTranslationOverrides & DocxTranslationOverrides;
  /**
   * Called as the deliverables are built and saved.
   *
   * WHY (2026-08-21 sweep): building the files and uploading them are THREE
   * long steps behind ONE static label — the PDF, the Word file, then an
   * upload that for the scanned record was 27MB. #989 gave the build a label
   * but nothing that moves, which the third instance of this defect taught us
   * is not progress.
   */
  onProgress?: (info: { stage: 'pdf' | 'docx' | 'saving'; pages?: number }) => void;
}

export type DocumentGeneratorParams = FormattedDocumentParams | ResearchDocumentParams;

// ============================================================================
// Helpers
// ============================================================================

/**
 * Serialize a File object to base64 dataUrl for localStorage persistence.
 */
export async function serializeFile(file: File): Promise<SerializedAttachment> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve({
        name: file.name,
        type: file.type,
        size: file.size,
        lastModified: file.lastModified,
        dataUrl: reader.result as string,
      });
    };
    reader.onerror = () => {
      // On error, return without dataUrl (download won't work but display will)
      resolve({
        name: file.name,
        type: file.type,
        size: file.size,
        lastModified: file.lastModified,
      });
    };
    reader.readAsDataURL(file);
  });
}

// ============================================================================
// Main Generator
// ============================================================================

/**
 * Generate documents in both PDF and DOCX formats.
 *
 * This is the primary entry point for dual-format document generation.
 * It ensures content parity between formats and handles serialization
 * for localStorage persistence.
 *
 * @example
 * ```typescript
 * const { pdf, docx, pdfSerialized, docxSerialized } = await generateDualFormatDocuments({
 *   type: 'formatted',
 *   content: formattedText,
 *   title: 'My Report',
 *   createdAt: new Date(),
 * });
 *
 * stream.finalize({
 *   attachments: [pdf, docx],
 *   attachmentsSerialized: [pdfSerialized, docxSerialized],
 * });
 * ```
 */
export async function generateDualFormatDocuments(
  params: DocumentGeneratorParams
): Promise<GeneratedDocuments> {
  console.log('[DocumentGenerator] Starting document generation:', {
    type: params.type,
    title: params.title,
    contentLength: params.content?.length,
  });

  let pdf: File;
  let docx: File;

  try {
  const { buildFormattedPdf, buildFormattedDocx, buildResearchPdf, buildResearchDocx } = await loadDocumentBuilders();
  if (params.type === 'formatted') {
    // Generate formatted documents (for wider audience)
    const pdfParams: BuildFormattedPdfParams = {
      content: params.content,
      title: params.title,
      createdAt: params.createdAt,
      translations: params.translations,
      includeCoverPage: params.includeCoverPage,
      sourceImages: params.sourceImages,
      onProgress: ({ pages }) => params.onProgress?.({ stage: 'pdf', pages }),
    };

    // Generate both formats in parallel
    [pdf, docx] = await Promise.all([
      buildFormattedPdf(pdfParams),
      buildFormattedDocx({
        content: params.content,
        title: params.title,
        createdAt: params.createdAt,
        translations: params.translations,
        includeCoverPage: params.includeCoverPage,
        // Both deliverables or neither: the PDF has carried the source's
        // figures since 2026-08-13 while the DOCX silently did not, so the
        // 200-page manual shipped a 7.9MB PDF of charts and a 105KB Word file
        // without one, from identical content.
        sourceImages: params.sourceImages,
      }),
    ]);
  } else {
    // Generate research documents (legal research format)
    [pdf, docx] = await Promise.all([
      buildResearchPdf({
        title: params.title,
        prompt: params.prompt,
        content: params.content,
        createdAt: params.createdAt,
        translations: params.translations,
      }),
      buildResearchDocx({
        title: params.title,
        prompt: params.prompt,
        content: params.content,
        createdAt: params.createdAt,
        translations: params.translations,
      }),
    ]);
  }

  console.log('[DocumentGenerator] PDF and DOCX files created successfully');

  // Serialize both files for localStorage persistence
  const [pdfSerialized, docxSerialized] = await Promise.all([
    serializeFile(pdf),
    serializeFile(docx),
  ]);

  console.log('[DocumentGenerator] Serialization complete:', {
    pdfName: pdf.name,
    pdfSize: pdf.size,
    docxName: docx.name,
    docxSize: docx.size,
    pdfHasDataUrl: !!pdfSerialized?.dataUrl,
    docxHasDataUrl: !!docxSerialized?.dataUrl,
  });

  return {
    pdf,
    docx,
    pdfSerialized,
    docxSerialized,
  };
  } catch (error) {
    console.error('[DocumentGenerator] Document generation FAILED:', error);
    throw error; // Re-throw to let caller handle
  }
}

/**
 * Generate only formatted documents (convenience wrapper).
 */
export async function generateFormattedDocuments(params: {
  content: string;
  title: string;
  createdAt?: Date;
  translations?: FormattedPdfTranslationOverrides & FormattedDocxTranslationOverrides;
}): Promise<GeneratedDocuments> {
  return generateDualFormatDocuments({
    type: 'formatted',
    ...params,
  });
}

/**
 * Generate only research documents (convenience wrapper).
 */
export async function generateResearchDocuments(params: {
  title: string;
  prompt: string;
  content: string;
  createdAt?: Date;
  translations?: PdfTranslationOverrides & DocxTranslationOverrides;
}): Promise<GeneratedDocuments> {
  return generateDualFormatDocuments({
    type: 'research',
    ...params,
  });
}

// ============================================================================
// Storage-Enabled Document Generation
// ============================================================================

/**
 * Options for uploading documents to Firebase Storage.
 */
export interface StorageUploadOptions {
  /** User ID (Firebase Auth UID) */
  uid: string;
  /** Unique message identifier for organizing storage */
  messageId: string;
}

/**
 * Upload status for tracking Storage upload success/failure.
 */
export interface StorageUploadStatus {
  /** Whether PDF was successfully uploaded to Storage */
  pdfUploaded: boolean;
  /** Whether DOCX was successfully uploaded to Storage */
  docxUploaded: boolean;
  /** Error message if upload(s) failed */
  error?: string;
}

/**
 * Result of dual-format document generation with Firebase Storage uploads.
 */
export interface GeneratedDocumentsWithStorage extends GeneratedDocuments {
  /** Serialized PDF with Firebase Storage URL (if upload succeeded) */
  pdfSerializedWithStorage?: SerializedAttachment;
  /** Serialized DOCX with Firebase Storage URL (if upload succeeded) */
  docxSerializedWithStorage?: SerializedAttachment;
  /** Upload status for error handling - indicates whether Storage uploads succeeded */
  uploadStatus?: StorageUploadStatus;
}

/**
 * Generate documents in both PDF and DOCX formats with optional Firebase Storage upload.
 *
 * This function extends generateDualFormatDocuments by uploading the generated
 * documents to Firebase Storage if storage options are provided. This enables
 * cross-device/cross-session document access.
 *
 * @example
 * ```typescript
 * const auth = getAuth();
 * const uid = auth.currentUser?.uid;
 *
 * const { pdf, docx, pdfSerializedWithStorage, docxSerializedWithStorage } =
 *   await generateDualFormatDocumentsWithStorage(
 *     { type: 'research', title: 'Report', prompt: '', content: text },
 *     uid ? { uid, messageId: stream.id } : undefined
 *   );
 *
 * stream.finalize({
 *   attachments: [pdf, docx],
 *   attachmentsSerialized: [
 *     pdfSerializedWithStorage || pdfSerialized,
 *     docxSerializedWithStorage || docxSerialized,
 *   ],
 * });
 * ```
 */
export async function generateDualFormatDocumentsWithStorage(
  params: DocumentGeneratorParams,
  storageOptions?: StorageUploadOptions
): Promise<GeneratedDocumentsWithStorage> {
  // Generate documents using the base function
  const result = await generateDualFormatDocuments(params);
  // Uploading is its own long step — 27MB over a domestic connection is not
  // instant, and it used to sit behind the same motionless label as the build.
  params.onProgress?.({ stage: 'saving' });

  // If no storage options provided, return base result with no uploadStatus
  if (!storageOptions) {
    return result;
  }

  // Dynamically import to avoid circular dependencies and bundle bloat
  // when Storage is not used
  const { serializeAttachmentsWithStorage } = await import('./attachmentStorage');

  try {
    // Upload both documents to Firebase Storage in parallel
    const [pdfWithStorage, docxWithStorage] = await serializeAttachmentsWithStorage(
      [result.pdf, result.docx],
      storageOptions.uid,
      storageOptions.messageId
    );

    // Validate uploads succeeded (storageUrl is present)
    const pdfUploaded = !!(pdfWithStorage?.storageUrl);
    const docxUploaded = !!(docxWithStorage?.storageUrl);

    if (!pdfUploaded || !docxUploaded) {
      console.error('[DocumentGenerator] Storage upload incomplete - CRITICAL:', {
        pdfUploaded,
        docxUploaded,
        pdfStorageUrl: pdfWithStorage?.storageUrl,
        docxStorageUrl: docxWithStorage?.storageUrl,
        pdfHasStoragePath: !!pdfWithStorage?.storagePath,
        docxHasStoragePath: !!docxWithStorage?.storagePath,
      });

      // Surface error in development
      if (process.env.NODE_ENV === 'development') {
        throw new Error('Storage upload failed - attachments will not persist across sessions');
      }
    } else {
      console.log('[DocumentGenerator] Attachments uploaded successfully:', {
        pdf: pdfWithStorage?.name,
        docx: docxWithStorage?.name,
        pdfUrl: pdfWithStorage?.storageUrl?.substring(0, 80),
        docxUrl: docxWithStorage?.storageUrl?.substring(0, 80),
      });
    }

    return {
      ...result,
      pdfSerializedWithStorage: pdfUploaded ? pdfWithStorage : undefined,
      docxSerializedWithStorage: docxUploaded ? docxWithStorage : undefined,
      uploadStatus: {
        pdfUploaded,
        docxUploaded,
        error: (!pdfUploaded || !docxUploaded) ? 'Partial upload - some files using local storage only' : undefined,
      },
    };
  } catch (error) {
    // Log error but don't fail - documents still work without Storage
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error('[DocumentGenerator] Storage upload failed completely:', errorMessage);
    return {
      ...result,
      uploadStatus: {
        pdfUploaded: false,
        docxUploaded: false,
        error: errorMessage,
      },
    };
  }
}
